import "./Details.css";

import React, {useCallback, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";

import {Category, Phrases, Attributes, ValidationRulesSearchStatus} from "../types";
import {validationRulesService} from "../services/validationRulesService";
import {DataLoadingStatus} from "shared/types/common";
import CategoryDetailsCard from "./CategoryDetailsCard";
import ResultsView from "./ResultsViewComponent";

const CategoryDetails = ({activeCategory, setActiveCategory}: {activeCategory: Category|undefined, setActiveCategory: React.Dispatch<React.SetStateAction<Category|undefined>>}) => {
    const navigate = useNavigate();
    const [dataLoadingStatus, setDataLoadingStatus] = useState<DataLoadingStatus>(DataLoadingStatus.Loaded);
    const [performAllSteps, setPerformAllSteps] = useState(false);
    const [phrases, setPhrases] = useState<Phrases|undefined>(undefined);
    const [attributes, setAttributes] = useState<Attributes|undefined>(undefined);

    const updateCategoryStatus = useCallback((newStatus: ValidationRulesSearchStatus) => {
        const newCategory: Category = {...activeCategory!, status: newStatus};
        setActiveCategory(newCategory);
    }, [activeCategory, setActiveCategory]);

    const scheduleUpdateStatus = useCallback((delay: number) => {
        setTimeout(() => {
            validationRulesService.fetchCategoryStatus(activeCategory!.cat_id).then(resp => {
            if (resp.errorCode === undefined && resp.success) {
                updateCategoryStatus(resp.status);
                if (resp.status > 0 && resp.status % 2 === 1) {
                    scheduleUpdateStatus(Math.min(delay*1.2, 20000));
                } else {
                    setDataLoadingStatus(DataLoadingStatus.Loaded);
                }
            } else {
                setDataLoadingStatus(DataLoadingStatus.Error);
            }
        });
        }, delay);
    }, [activeCategory, updateCategoryStatus]);

    const nextStep = useCallback(() => {
        setDataLoadingStatus(DataLoadingStatus.Loading);
        validationRulesService.performStepForCategory(activeCategory!.cat_id).then(resp => {
            if (resp.errorCode === undefined && resp.success) {
                updateCategoryStatus(resp.status);
                if (resp.long_running) {
                    setDataLoadingStatus(DataLoadingStatus.IdleLoading);
                    scheduleUpdateStatus(5000);
                } else {
                    setDataLoadingStatus(DataLoadingStatus.Loaded);
                }
            } else {
                setDataLoadingStatus(DataLoadingStatus.Error);
            }
        });
    }, [activeCategory, updateCategoryStatus, scheduleUpdateStatus]);

    const nextAllSteps = useCallback(() => {
        setPerformAllSteps(true);
    }, []);

    useEffect(() => {
        if (performAllSteps && dataLoadingStatus === DataLoadingStatus.Loaded) {
            if (activeCategory?.status === ValidationRulesSearchStatus.Attributes_Found) {
                setPerformAllSteps(false);
            } else {
                nextStep();
            }
        }
    }, [nextStep, performAllSteps, dataLoadingStatus, activeCategory?.status]);

    const reloadCategory = useCallback(() => {
        setDataLoadingStatus(DataLoadingStatus.Loading);
        validationRulesService.restartCategory(activeCategory!.cat_id).then(resp => {
            if (resp.errorCode === undefined && resp.success) {
                updateCategoryStatus(resp.status);
                if (resp.long_running) {
                    setDataLoadingStatus(DataLoadingStatus.IdleLoading);
                    scheduleUpdateStatus(5000);
                } else {
                    setDataLoadingStatus(DataLoadingStatus.Loaded);
                    setPhrases(undefined);
                    setAttributes(undefined);
                }
            } else {
                setDataLoadingStatus(DataLoadingStatus.Error);
            }
        });
    }, [activeCategory, updateCategoryStatus, scheduleUpdateStatus]);

    useEffect(() => {
        setPhrases(undefined);
        setAttributes(undefined);
    }, [activeCategory]);

    if (dataLoadingStatus === DataLoadingStatus.Loaded && activeCategory && activeCategory.status >= ValidationRulesSearchStatus.Phrases_Found && phrases === undefined) {
        setDataLoadingStatus(DataLoadingStatus.Loading);
        validationRulesService.getCategoryPhrases(activeCategory!.cat_id).then(resp => {
            if (resp.errorCode === undefined && resp.success) {
                setPhrases(resp.phrases!);
                setDataLoadingStatus(DataLoadingStatus.Loaded);
            } else {
                setDataLoadingStatus(DataLoadingStatus.Error);
            }
        });
    }

    if (dataLoadingStatus === DataLoadingStatus.Loaded && activeCategory && activeCategory.status >= ValidationRulesSearchStatus.Attributes_Found && attributes === undefined) {
        setDataLoadingStatus(DataLoadingStatus.Loading);
        validationRulesService.getCategoryAttributes(activeCategory!.cat_id).then(resp => {
            if (resp.errorCode === undefined && resp.success) {
                setAttributes(resp.attributes!);
                setDataLoadingStatus(DataLoadingStatus.Loaded);
            } else {
                setDataLoadingStatus(DataLoadingStatus.Error);
            }
        });
    }

    return (
        <div className="details-component scrollable">
            {activeCategory === undefined?
                <p className="select-category-message">Select the category...</p> :
                <div className="details-wrapper">
                    <CategoryDetailsCard category={activeCategory!}/>
                    <div className="category-actions-button-menu">
                        <div className="category-actions-button-wrapper">
                            <button className="button green-button"
                                    disabled={dataLoadingStatus === DataLoadingStatus.Loading || dataLoadingStatus === DataLoadingStatus.IdleLoading || activeCategory.status === ValidationRulesSearchStatus.Attributes_Found}
                                    onClick={nextStep}
                            >Perform 1 step</button>
                        </div>
                        <div className="category-actions-button-wrapper">
                            <button className="button green-button"
                                    disabled={dataLoadingStatus === DataLoadingStatus.Loading || dataLoadingStatus === DataLoadingStatus.IdleLoading || activeCategory.status === ValidationRulesSearchStatus.Attributes_Found}
                                    onClick={nextAllSteps}
                            >Perform all steps</button>
                        </div>
                        <div className="category-actions-button-wrapper">
                            <button className="button red-button"
                                    disabled={dataLoadingStatus === DataLoadingStatus.Loading || dataLoadingStatus === DataLoadingStatus.IdleLoading || activeCategory.status === ValidationRulesSearchStatus.Created}
                                    onClick={reloadCategory}
                            >Reload excel data</button>
                        </div>
                        <div className="category-actions-button-wrapper">
                            <button className="button green-button"
                                    disabled={dataLoadingStatus === DataLoadingStatus.Loading || dataLoadingStatus === DataLoadingStatus.IdleLoading}
                                    onClick={() => navigate(`/excel-diff?cat_num=${activeCategory?.cat_num}`)}
                            >View Excel</button>
                        </div>
                        <div className="category-actions-button-wrapper">
                            <button className="button green-button"
                                    disabled={dataLoadingStatus === DataLoadingStatus.Loading || dataLoadingStatus === DataLoadingStatus.IdleLoading}
                                    onClick={() => navigate(`/pvr/?cat_num=${activeCategory?.cat_num}`)}
                            >View PVR</button>
                        </div>
                    </div>
                    {dataLoadingStatus === DataLoadingStatus.Loading? <p className="info-text">It may take up to 5 minutes. Please, be patient and don't refresh the page!</p> : <></>}
                    {dataLoadingStatus === DataLoadingStatus.IdleLoading? <p className="info-text">It may take up to 40 minutes. Please, be patient and don't refresh the page!</p> : <></>}
                    {dataLoadingStatus === DataLoadingStatus.Error? <p className="error-text">Network error occured! Please, reload the page and try again...</p> : <></>}
                    <div className="results-wrapper">
                        {phrases || attributes? <ResultsView cat_id={activeCategory?.cat_id ?? -1} phrases={phrases} attributes={attributes}/> : <></>}
                    </div>
                </div>
            }</div>
    );
};

export default CategoryDetails;
